import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Badge } from '@/components/lemmala-design-system/badge/badge'
import { CheckboxWithLabel } from '@/components/lemmala-design-system/checkbox/checkbox'
import { CommandItem } from '@/components/lemmala-design-system/command/command'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

import { DropdownSelectContext } from './dropdown-select-context'
import type { DropdownSelectItem } from './dropdown-select.types'

import CheckIcon from '@/assets/dropdown-check.svg'

// Sortable item used when `draggable` is true
interface SortableDropdownSelectItemProps {
  item: DropdownSelectItem
}

export const SortableDropdownSelectItem = ({ item }: SortableDropdownSelectItemProps) => {
  const context = React.useContext(DropdownSelectContext)
  if (!context) {
    throw new Error('SortableDropdownSelectItem must be used within DropdownSelect')
  }

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const { multi, selectedItems = [], onItemSelect, onItemToggle, showTooltips } = context
  const isSelected = selectedItems.includes(item.id)

  const handleClick = () => {
    if (item.disabled) return
    if (multi && onItemToggle) {
      onItemToggle(item.id, !isSelected)
    } else if (onItemSelect) {
      onItemSelect(item.id)
    }
  }

  return (
    <div ref={setNodeRef} style={style} className={cn(isDragging && 'opacity-50')}>
      <CommandItem
        value={item.id}
        disabled={item.disabled}
        onMouseDown={(e) => {
          e.preventDefault()
          if (!item.disabled) {
            handleClick()
          }
        }}
        className={cn(
          'cursor-pointer px-0.5 text-body-default',
          !item.disabled && 'hover:!bg-main-background-interactive-list-hover',
          isSelected &&
            !multi &&
            'bg-main-background-interactive-list-selected hover:!bg-main-background-interactive-list-selectedHover',
          item.disabled && 'text-main-foreground-disabled opacity-50',
        )}
      >
        <div
          className={cn(
            'mr-1.5 flex items-center',
            item.disabled ? 'text-main-foreground-disabled' : 'text-main-foreground-tertiary',
          )}
          // Prevent selection toggle when starting a drag from the handle
          onMouseDown={(e) => e.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          <GripVertical className='size-4 shrink-0' />
        </div>

        {multi ? (
          <CheckboxWithLabel
            disabled={item.disabled}
            checked={isSelected}
            className='text-label-default'
          >
            <Tooltip
              trigger={<SortableDropdownSelectItemContent item={item} />}
              tooltipDisabled={!showTooltips}
            >
              {item.value || item.label}
            </Tooltip>
          </CheckboxWithLabel>
        ) : (
          <div className='flex w-full items-center justify-between'>
            <Tooltip
              trigger={<SortableDropdownSelectItemContent item={item} />}
              tooltipDisabled={!showTooltips}
            >
              {item.value || item.label}
            </Tooltip>
            {isSelected && <CheckIcon className='size-4 text-main-foreground-accent' />}
          </div>
        )}
      </CommandItem>
    </div>
  )
}

interface SortableDropdownSelectItemContentProps {
  item: DropdownSelectItem
}

const SortableDropdownSelectItemContent = ({ item }: SortableDropdownSelectItemContentProps) => {
  if (item.type === 'badge') {
    return (
      <Badge variant='attention' size='default' badgeStyle='subtle'>
        {item.label}
      </Badge>
    )
  }

  return (
    <div className='flex items-center gap-2'>
      {item.icon && <span className='flex items-center'>{item.icon}</span>}
      <span
        className={cn(
          'text-body-default',
          item.disabled && 'text-main-foreground-disabled',
          !item.disabled && 'text-main-foreground-primary',
        )}
      >
        {item.label}
      </span>
    </div>
  )
}
SortableDropdownSelectItemContent.displayName = 'SortableDropdownSelectItemContent'
